import { useState, useEffect } from "react";
import { pb, TutorProfile } from "@/lib/pocketbase";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export const useTutorProfile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<TutorProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [approvedSubjects, setApprovedSubjects] = useState<string[]>([]);

  // Fetch approved subjects when hook initializes
  useEffect(() => {
    const fetchApprovedSubjects = async () => {
      try {
        const records = await pb.collection("approved_subjects").getList(1, 100, {
          filter: "isActive = true",
          sort: "name",
        });
        const subjects = records.items.map((item) => item.name);
        setApprovedSubjects(subjects);
      } catch (error) {
        setApprovedSubjects([]);
      }
    };

    fetchApprovedSubjects();
  }, []);

  // Get all available subjects (default + approved)
  const getAllAvailableSubjects = () => {
    const defaultSubjects = [
      "Mathematics",
      "English",
      "Spanish",
      "Physics",
      "Chemistry",
      "Biology",
      "Literature",
      "English Language",
      "French",
      "German",
      "Italian",
      "Portuguese",
      "Chinese",
      "Japanese",
      "Korean",
      "Arabic",
      "Programming",
      "Business",
      "Marketing",
      "Finance",
      "Data Science",
      "Web Development",
      "Graphic Design",
      "Fitness & Bodybuilding",
      "Cooking",
      "Music",
      "Art",
      "Photography",
      "Travel",
      "Conversation",
    ];

    // Combine default subjects with approved subjects, removing duplicates
    const allSubjects = [...defaultSubjects];
    approvedSubjects.forEach((subject) => {
      if (!allSubjects.includes(subject)) {
        allSubjects.push(subject);
      }
    });

    return allSubjects.sort();
  };

  useEffect(() => {
    setIsLoading(false);
    setProfile(null);
  }, []);

  const findExistingProfile = async (userId: string) => {
    try {
      const existingRecords = await pb.collection("tutors").getList(1, 1, {
        filter: `user = "${userId}"`,
      });

      if (existingRecords.items.length > 0) {
        return existingRecords.items[0];
      }
      return null;
    } catch (error) {
      console.error("Error finding existing profile:", error);
      return null;
    }
  };

  const createDefaultProfile = async () => {
    try {
      if (!user?.id) {
        throw new Error("No user available");
      }

      console.log("Creating default profile for user:", user.id);
      const newProfile = await pb.collection("tutors").create({
        user: user.id,
        name: user.name || "",
        bio: "",
        shortBio: "",
        subjects: [],
        hourlyRate: 0,
        languages: [],
        experience: "",
        education: [],
        availability: { days: [], timeSlots: [] },
        country: "", // Ensure country field is included
        is_suspended: false, // Ensure profile is not suspended by default
        suspended_at: null, // No suspension date
      });

      console.log("Created new profile:", newProfile);
      return newProfile as TutorProfile;
    } catch (error) {
      console.error("Error creating default profile:", error);
      throw error;
    }
  };

  const updateProfile = async (data: Partial<TutorProfile>) => {
    try {
      console.log("Updating profile with data:", data);
      console.log("Current profile state:", profile);

      if (!user?.id) {
        throw new Error("No user available");
      }

      // Always check if a profile exists in the database first, regardless of local state
      console.log("Checking for existing profile in database for user:", user.id);
      const existingProfile = await findExistingProfile(user.id);

      let updatedProfile;

      if (existingProfile) {
        // Update existing profile - ensure country is included
        console.log("Updating existing profile with ID:", existingProfile.id);
        const updateData = {
          ...data,
          // Ensure country is explicitly included if provided
          ...(data.country !== undefined && { country: data.country }),
        };
        console.log("Update data being sent to PocketBase:", updateData);
        updatedProfile = await pb.collection("tutors").update(existingProfile.id, updateData);
        console.log("Profile updated successfully:", updatedProfile);
      } else {
        // Create new profile
        console.log("No existing profile found, creating new one");
        console.log("🔍 AUTH STATE BEFORE PROFILE CREATE:");
        console.log("  - PB Auth valid:", pb.authStore.isValid);
        console.log("  - PB Auth token exists:", !!pb.authStore.token);
        console.log("  - PB Auth user ID:", pb.authStore.model?.id);
        console.log("  - Target user ID:", user.id);

        // Create a minimal profile first, then update with data
        const minimalProfileData = {
          user: user.id,
          name: data.name || "",
          bio: data.bio || "",
          shortBio: data.shortBio || "",
          subjects: data.subjects || [],
          hourlyRate: data.hourlyRate || 0,
          languages: data.languages || [],
          experience: data.experience || "",
          education: data.education || [],
          availability: data.availability || { days: [], timeSlots: [] },
          country: data.country || "",
          rating: 5, // Default rating
          reviewCount: 0,
          is_suspended: false, // Ensure profile is not suspended by default
          suspended_at: null, // No suspension date
        };

        console.log("Creating profile with minimal data:", minimalProfileData);
        updatedProfile = await pb.collection("tutors").create(minimalProfileData);
        console.log("New profile created successfully:", updatedProfile);
      }

      setProfile(updatedProfile as TutorProfile);

      toast({
        title: "Success",
        description: "Profile updated successfully!",
      });

      return updatedProfile;
    } catch (error: any) {
      console.error("Error updating profile:", error);
      console.error("Error details:", error.response || error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
      throw error;
    }
  };

  const uploadProfilePicture = async (file: File) => {
    try {
      // Validate file type before uploading
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      console.log("File type:", file.type);

      if (!allowedTypes.includes(file.type)) {
        throw new Error(`Unsupported file type: ${file.type}. Allowed types: ${allowedTypes.join(", ")}`);
      }

      // Check file size (limit to 5MB)
      const maxSize = 50 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum size: 5MB`);
      }

      if (!user?.id) {
        throw new Error("No user available");
      }

      // Find existing profile - don't create a new one
      const existingProfile = await findExistingProfile(user.id);

      if (!existingProfile) {
        throw new Error("No tutor profile found. Please create your profile first before uploading a picture.");
      }

      console.log("Uploading file to existing profile ID:", existingProfile.id);

      // Create FormData with the exact field name expected by PocketBase
      const formData = new FormData();
      formData.append("profilePicture", file, file.name);

      // Clear the URL field when uploading a file
      formData.append("profilePictureUrl", "");

      // Upload the profile picture using PocketBase's update method
      const updatedProfile = await pb.collection("tutors").update(existingProfile.id, formData);

      console.log("Profile updated successfully with file upload:", updatedProfile);

      setProfile(updatedProfile as TutorProfile);

      toast({
        title: "Success",
        description: "Profile picture uploaded successfully!",
      });

      return updatedProfile;
    } catch (error: any) {
      console.error("Profile picture upload error:", error);

      toast({
        title: "Error",
        description: error.response?.data?.message || error.message || "Failed to upload profile picture",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateProfilePictureUrl = async (imageUrl: string) => {
    try {
      if (!user?.id) {
        throw new Error("No user available");
      }

      // Find existing profile - don't create a new one
      const existingProfile = await findExistingProfile(user.id);

      if (!existingProfile) {
        throw new Error("No tutor profile found. Please create your profile first before updating the picture.");
      }

      console.log("Updating profile picture with URL for existing profile:", existingProfile.id);
      console.log("URL being saved:", imageUrl);
      console.log("Current profile data before update:", existingProfile);

      // Validate URL format
      if (!imageUrl.trim()) {
        throw new Error("Profile picture URL cannot be empty");
      }

      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(imageUrl.trim())) {
        throw new Error("Please provide a valid URL starting with http:// or https://");
      }

      // Prepare update data
      const updateData = {
        profilePictureUrl: imageUrl.trim(),
        profilePicture: null, // Clear the file field when using URL
      };

      console.log("Update data being sent:", updateData);

      // Update profile with URL in the profilePictureUrl field and clear the file field
      const updatedProfile = await pb.collection("tutors").update(existingProfile.id, updateData);

      console.log("Profile updated with URL:", updatedProfile);

      setProfile(updatedProfile as TutorProfile);

      toast({
        title: "Success",
        description: "Profile picture updated successfully!",
      });

      return updatedProfile;
    } catch (error: any) {
      console.error("Profile picture URL update error:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.status,
        response: error.response,
        data: error.data,
      });

      let errorMessage = "Failed to update profile picture";

      if (error.status === 400) {
        console.log("400 error details:", error.response);
        if (error.response?.data) {
          const errorData = error.response.data;
          console.log("Error data structure:", errorData);

          // Check for specific field validation errors
          if (errorData.profilePictureUrl) {
            errorMessage = `Profile picture URL error: ${errorData.profilePictureUrl.message || errorData.profilePictureUrl}`;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else {
            errorMessage = "Invalid data provided. Please check the URL format.";
          }
        } else {
          errorMessage = "Invalid request. Please check the URL format and try again.";
        }
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    }
  };

  const getProfilePictureUrl = (profile: TutorProfile | null) => {
    if (!profile) {
      return null;
    }

    // First check if there's a URL stored and it's a valid URL
    if (profile.profilePictureUrl && profile.profilePictureUrl.trim()) {
      const url = profile.profilePictureUrl.trim();
      // Check if it's a valid URL (starts with http or https)
      if (url.startsWith("http://") || url.startsWith("https://")) {
        return url;
      }
    }

    // Then check if there's a file uploaded
    if (profile.profilePicture && profile.profilePicture.trim()) {
      try {
        const fileUrl = pb.files.getURL(profile, profile.profilePicture);
        return fileUrl;
      } catch (error) {
        return null;
      }
    }

    return null;
  };

  return {
    profile,
    isLoading,
    approvedSubjects,
    getAllAvailableSubjects,
    updateProfile,
    uploadProfilePicture,
    updateProfilePictureUrl,
    getProfilePictureUrl,
  };
};
